/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { qanda } from './qanda'
import {
  EstimateQuestion,
  SingleChoice,
  Slider,
  SwissTileMap,
  ColumnChart,
  BarChart,
  Summary,
  SwissTileResolve,
  voteComparison,
  voteQuality,
  convertPercentToWords,
  capitalize
} from 'components/UserEstimates'
import { GlobalColors } from 'components/taCSScolors'

// TODO Set the correct article ID here
const ARTICLE_ID = '125556564395'

const nf = new Intl.NumberFormat('fr-CH')

export function AppComponent() {
  const [questionAnswered, setAnswered] = useState<string[]>([])
  const handleAnswer = (questionid: string) => {
    setAnswered(questionAnswered => [...questionAnswered, questionid])
  }

  const valueFormat = (val: any) => {
    return val > 9999 ? nf.format(parseInt(val)) : val
  }

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <GlobalColors />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Corruption: devinez le prix de ces montres suisses</h1>
            <h2>
              Au Venezuela, le maître des finances du régime s’est offert une
              fabuleuse collection de pièces horlogères helvétiques. Saurez-vous
              évaluer leur prix?
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Christian Brönnimann, Sylvain Besson et Patrick Vögeli
            </div>
            <div className='ta updated'>Mis à jour le 17 janvier 2021</div>
          </div>
          <div className='ta text'>
            <p>
              L’ancien directeur du Trésor public vénézuélien, Alejandro
              Andrade, s’est fabuleusement enrichi grâce à la corruption. En
              2018, il a été condamné à 10 ans de prison aux États-Unis. Il a
              alors reconnu avoir reçu environ un milliard de dollars de
              pots-de-vin. La justice américaine a identifié de nombreux biens
              qu’il a achetés grâce à la corruption, dont huit propriétés en
              Floride et au Venezuela, 17 chevaux de course, dix voitures, trois
              jets privés et 35 montres de luxe suisses.
            </p>
            <p>
              Serez-vous capable d’évaluer ces pièces horlogères d’exception?
              Voici cinq des plus belles montres d’Alejandro Andrade, dont vous
              pouvez deviner le prix.
            </p>
          </div>
        </div>
      </div>

      <div id='quizSection'>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[0].clockType}</div>
                <img
                  src={require(`../../images/jaeger.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[0].id}
                question={qanda[0].question}
                correctAnswer={qanda[0].correctAnswer}
                ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 500000]}
                    step={5000}
                    unit={qanda[0].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              La bonne réponse est &nbsp;
                              {valueFormat(props.correctAnswer)} francs.
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> des{' '}
                                <b>{total} participants</b>{' '}
                                ont également trouvé la bonne réponse
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> des{' '}
                                <b>{total} participants</b> étaient plus proches de cette estimation.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 500000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[0].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[1].clockType}</div>
                <img
                  src={require(`../../images/rolex.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[1].id}
                question={qanda[1].question}
                correctAnswer={qanda[1].correctAnswer}
                ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 500000]}
                    step={5000}
                    unit={qanda[1].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              La bonne réponse est &nbsp;
                              {valueFormat(props.correctAnswer)} francs.
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> des{' '}
                                <b>{total} participants</b>{' '}
                                ont également trouvé la bonne réponse
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> des{' '}
                                <b>{total} participants</b> étaient plus proches de cette estimation.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 500000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[1].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[2].clockType}</div>
                <img
                  src={require(`../../images/greubel-forsey.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[2].id}
                question={qanda[2].question}
                correctAnswer={qanda[2].correctAnswer}
                ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 500000]}
                    step={5000}
                    unit={qanda[2].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              La bonne réponse est&nbsp;
                              {valueFormat(props.correctAnswer)} francs.
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> des{' '}
                                <b>{total} participants</b>{' '}
                                ont également trouvé la bonne réponse
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> des{' '}
                                <b>{total} participants</b> étaient plus proches de cette estimation.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 500000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[2].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[3].clockType}</div>
                <img
                  src={require(`../../images/hublot.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[3].id}
                question={qanda[3].question}
                correctAnswer={qanda[3].correctAnswer}
                ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 500000]}
                    step={5000}
                    unit={qanda[3].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              La bonne réponse est &nbsp;
                              {valueFormat(props.correctAnswer)} francs.
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> des{' '}
                                <b>{total} participants</b>{' '}
                                ont également trouvé la bonne réponse
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> des{' '}
                                <b>{total} participants</b> étaient plus proches de cette estimation.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 500000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[3].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[4].clockType}</div>
                <img
                  src={require(`../../images/patek.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[4].id}
                question={qanda[4].question}
                correctAnswer={qanda[4].correctAnswer}
                ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 500000]}
                    step={5000}
                    unit={qanda[4].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              La bonne réponse est &nbsp;
                              {valueFormat(props.correctAnswer)} francs.
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> des{' '}
                                <b>{total} participants</b>{' '}
                                ont également trouvé la bonne réponse
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> des{' '}
                                <b>{total} participants</b> étaient plus proches de cette estimation.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 500000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[4].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer
        mailTo='sylvain.besson@tamedia.ch'
        title='Comment jugez-vous ce contenu?'
        locale='fr'
        hidePortfolioTeaser
        articleId={ARTICLE_ID}
        questions={[
          'Était-il divertissant?',
          'La mise en œuvre technique a-t-elle fonctionné?'
        ]}
        credits={[
          ['Texte et concept', 'Christian Brönnimann et Sylvain Besson'],
          ['Conception', 'Patrick Vögeli']
        ]}
      />
    </div>
  )
}
